<script lang="ts" setup>
/* Store */
import { useToggleStore } from '@/store/toggle/toggle'
import { useCityStore } from '@/store/city/city'

/* Components */
import AppHead from '@/components/App/AppHead.vue'
import HalvesPizzaList from '@/components/Halves/HalvesPizzaList.vue'
import HalvesContainer from '@/components/Halves/HalvesContainer.vue'

/* Types */
import { ConstructorPizza, ProductPizza } from '@/types/api/api-v3/halves'
import { HttpStatusCode, NotFoundError, ServerError } from '@/api/results'

const toggleStore = useToggleStore()
const {
  cityInfo: { domain },
} = useCityStore()

const { $apiService } = useNuxtApp()

const halvesEnabled = computed(() => toggleStore.featureToggle.halves_enabled)

const { data: isHalvesPizza, error } = await useAsyncData(async (nuxtApp) => {
  if (!halvesEnabled.value) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  const result = await nuxtApp?.$apiService.halves.fetchHalvesPizze()

  if (result instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  return result?.data
})

if (error.value) showError(error.value)

const listPizza = ref([] as ProductPizza[])

const constructorPizza = ref({
  leftHalve: null,
  rightHalve: null,
} as ConstructorPizza)

const wholePizza = computed(() => {
  if (
    constructorPizza.value.leftHalve?.id ===
    constructorPizza.value.rightHalve?.id
  ) {
    return listPizza.value.find(
      (product: ProductPizza) =>
        constructorPizza.value.leftHalve?.parent_id === product.detail_id,
    )
  } else {
    // eslint-disable-next-line array-callback-return
    return listPizza.value.find(({ components }) => {
      if (components?.length) {
        return components.every(
          ({ product_id }) =>
            product_id === constructorPizza?.value.leftHalve?.id ||
            product_id === constructorPizza?.value.rightHalve?.id,
        )
      }
    })
  }
})

onMounted(async () => {
  // @ts-ignore
  const result = await $apiService.halves.fetchListPizza()

  if (
    result.value instanceof NotFoundError ||
    result.value instanceof ServerError
  )
    return

  listPizza.value = result.data.products
})

const addHalveInConstructor = (halve: ProductPizza) => {
  !constructorPizza.value.leftHalve
    ? (constructorPizza.value.leftHalve = halve)
    : (constructorPizza.value.rightHalve = halve)
}
const removeHalveOfConstructor = (side: string) => {
  // @ts-ignore
  constructorPizza.value[side] = null
}
</script>

<template>
  <div class="page-halves">
    <app-head
      :title="isHalvesPizza?.seo?.seo_title"
      :seo="{
        title: isHalvesPizza?.seo?.seo_title,
        description: isHalvesPizza?.seo?.seo_description,
        keywords: isHalvesPizza?.seo?.seo_keywords,
      }"
    />

    <the-breadcrumbs
      :breadcrumbs="isHalvesPizza?.breadcrumbs"
      :domain="domain"
    />

    <h1 class="page-halves__title">
      {{ isHalvesPizza?.name }}
    </h1>

    <div class="page-halves__wrapper">
      <halves-pizza-list
        :constructor-pizza="constructorPizza"
        :products="isHalvesPizza?.products"
        @add-halve-in-constructor="addHalveInConstructor"
        @remove-halve-of-constructor="removeHalveOfConstructor"
      />
      <halves-container
        :whole-pizza="wholePizza"
        :constructor-pizza="constructorPizza"
        @remove-halve-of-constructor="removeHalveOfConstructor"
      />
    </div>

    <app-seo-description
      v-if="isHalvesPizza?.seo.seo_text"
      class="category__seo"
      :seo-content="isHalvesPizza?.seo.seo_text"
    />
  </div>
</template>

<style lang="scss" scoped>
.page-halves {
  padding-top: 40px;

  &__title {
    margin-top: 30px;
    font-size: 28px;
    font-weight: 900;
    line-height: 33px;
  }

  &__wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
    margin: 60px 0 80px 0;
  }

  @include media('<desktop-mini') {
    padding: 20px 48px 0;
  }

  @include media('<=desktop-mini') {
    &__wrapper {
      flex-direction: column;
      margin: 40px 0 80px 0;
    }

    &__title {
      margin-top: 30px;
      font-size: 24px;
      line-height: 48px;
    }
  }

  @include media('<=portrait-phone') {
    padding: 20px 20px 0;
    &__wrapper {
      flex-direction: column;
      margin: 24px 0 80px 0;
    }
    &__title {
      margin-top: 24px;
      font-size: 18px;
      line-height: 30px;
    }
  }
}
</style>
